import type React from 'react';
import { PageTitle } from '@/hooks/usePageTitle';

// When the policy text last changed. Update this whenever the wording below changes.
const LAST_UPDATED = 'October 13, 2026';

const Privacy: React.FC = () => (
  <div className="mx-auto max-w-3xl px-4 sm:px-8 page-top">
    <PageTitle title="Privacy policy" />
    <h1 className="font-display text-3xl font-bold tracking-tight text-grey-900 mb-6">Privacy Policy</h1>
    <div className="prose prose-grey max-w-none">
      <p className="text-muted-foreground">
        Last updated: {LAST_UPDATED}
      </p>
      <p className="mt-4">
        Showliner is a product of Swavely Creative Co. Showliner ("we", "us", or "our") operates the
        showliner.io website and related services. This page informs you of our policies regarding the
        collection, use, and disclosure of personal data when you use our Service.
      </p>
      <h2 className="font-heading text-xl font-semibold mt-8 mb-3">Information We Collect</h2>
      <p>
        We collect information you provide directly, including your name, email address, and profile
        information when you create an account. If you choose to give us a mobile phone number, we
        collect that too (see Text Messages below). We also collect usage data such as pages visited
        and features used.
      </p>
      <h2 className="font-heading text-xl font-semibold mt-8 mb-3">How We Use Your Information</h2>
      <p>
        We use your information to provide and improve the Service, communicate with you, and ensure
        the security of our platform.
      </p>

      <h2 className="font-heading text-xl font-semibold mt-8 mb-3">Text Messages (SMS)</h2>
      <p>
        Providing a mobile number is always optional, and we only text a number that was given to us
        for that purpose. We use text messages for:
      </p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>
          <strong>Open mic alerts:</strong> if you sign up to perform and opt in, we text you when
          you are next on the lineup and to confirm your signup.
        </li>
        <li>
          <strong>Event and workspace invitations:</strong> if an organizer you are working with
          adds your number, we text you the invitation link.
        </li>
        <li>
          <strong>Account security:</strong> one-time codes to verify your number and to sign in
          when you turn on two-factor authentication.
        </li>
      </ul>
      <p className="mt-4">
        <strong>
          We do not sell, rent, or share mobile phone numbers or SMS consent with third parties for
          marketing purposes.
        </strong>{' '}
        Numbers are shared only with the messaging provider that delivers the message on our behalf,
        and only for that delivery. We do not send marketing or promotional text messages.
      </p>
      <p className="mt-4">
        Message frequency varies by how you use the Service. Message and data rates may apply. You
        can opt out at any time by replying <strong>STOP</strong> to any message, and get help by
        replying <strong>HELP</strong>. Opting out of texts does not affect your account; we will
        still email you where relevant. You can also remove your number in your account settings.
      </p>
      <p className="mt-4">
        Text messaging is currently available to US and Canadian numbers only.
      </p>

      <h2 className="font-heading text-xl font-semibold mt-8 mb-3">The Showliner App for iPhone</h2>
      <p>
        The app uses the same account as the website and shows you the same information. A few
        things work differently on a phone:
      </p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>
          <strong>Signing in:</strong> you sign in on a Showliner web page that opens inside the
          app. The app then keeps a sign-in token in your iPhone's keychain, so the share extension
          ("Share to Showliner") can use your account too. Signing out deletes it.
        </li>
        <li>
          <strong>Push notifications:</strong> if you allow them, we store your device's push
          token, the app version and your notification settings (including quiet hours) so we can
          send notifications to that phone. Apple's push service delivers them. Signing out removes
          the token from our records.
        </li>
        <li>
          <strong>Kept on your phone:</strong> the app keeps a copy of what you last saw, and the
          set lists and charts you have opened, so they still open without a signal. Both are
          deleted when you sign out.
        </li>
        <li>
          <strong>Photos and files:</strong> the app only reads a photo or file you pick or share
          to it, and uploads it to the show you chose. Uploaded files are stored at an unlisted web
          address: anyone who has the address can open it.
        </li>
        <li>
          <strong>No tracking:</strong> the app has no ads, no advertising identifiers and no
          third-party analytics, and we do not use your data to track you across other apps or
          websites.
        </li>
      </ul>

      <h2 className="font-heading text-xl font-semibold mt-8 mb-3">Deleting Your Account</h2>
      <p>
        You can delete your account in the app (Account, then Delete Account) or in your account
        settings on the website. Your account is closed straight away and we email you a link to
        restore it. After 30 days we erase your personal details. Records other people rely on, such
        as a show you played on someone else's bill or a payment kept for tax purposes, are kept
        without your personal details.
      </p>

      <h2 className="font-heading text-xl font-semibold mt-8 mb-3">Contact</h2>
      <p>
        Questions about this policy or your data:{' '}
        <a href="mailto:hello@showliner.io" className="underline">hello@showliner.io</a>.
      </p>
    </div>
  </div>
);

export default Privacy;
